import { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { GitCommitHorizontal, MessageSquareText } from 'lucide-react';
import {
  REMARK_STATUSES,
  severityClassName,
  type Remark,
  type RemarkStatus,
} from '@/entities/remarks';
import { labelOf, useI18n } from '@/shared/i18n';
import { cn } from '@/shared/lib/utils';
import { Badge } from '@/shared/ui/shadcn/badge';
import { ScrollArea } from '@/shared/ui/shadcn/scroll-area';

export interface RemarksBoardProps {
  remarks: Remark[];
  onOpen: (remark: Remark) => void;
  onMove: (remark: Remark, status: RemarkStatus) => void;
  busyIds?: Set<string>;
  className?: string;
}

function RemarkCardBody({ remark, dragging }: { remark: Remark; dragging?: boolean }) {
  const { t } = useI18n();
  return (
    <div
      className={cn(
        'space-y-1.5 rounded-md border border-border bg-background p-2.5 text-xs shadow-xs',
        dragging && 'shadow-lg ring-2 ring-primary/40',
      )}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant="outline" className={cn('font-normal', severityClassName(remark.severity))}>
          {labelOf(t.remarks.severity, remark.severity)}
        </Badge>
        <Badge variant="outline" className="font-normal">
          {labelOf(t.remarks.categories, remark.category)}
        </Badge>
      </div>
      <p className="text-sm leading-snug font-medium text-foreground">{remark.title}</p>
      <p className="line-clamp-3 text-muted-foreground">{remark.body}</p>
      <div className="flex items-center gap-2 pt-0.5 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <MessageSquareText className="size-3" />
          {remark.dialog_id.slice(0, 8)}
        </span>
        {remark.commit_ref ? (
          <span className="inline-flex items-center gap-1 font-mono">
            <GitCommitHorizontal className="size-3" />
            {remark.commit_ref.slice(0, 10)}
          </span>
        ) : null}
        <span className="ml-auto">{new Date(remark.updated_at).toLocaleDateString()}</span>
      </div>
    </div>
  );
}

function DraggableCard({
  remark,
  onOpen,
  disabled,
}: {
  remark: Remark;
  onOpen: (remark: Remark) => void;
  disabled?: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: remark.id,
    data: { remark },
    disabled,
  });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(remark)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onOpen(remark);
      }}
      className={cn(
        'cursor-grab touch-none outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isDragging && 'opacity-40',
        disabled && 'cursor-progress opacity-60',
      )}
    >
      <RemarkCardBody remark={remark} />
    </div>
  );
}

function Column({
  status,
  remarks,
  onOpen,
  busyIds,
}: {
  status: RemarkStatus;
  remarks: Remark[];
  onOpen: (remark: Remark) => void;
  busyIds?: Set<string>;
}) {
  const { t } = useI18n();
  const { setNodeRef, isOver } = useDroppable({ id: status });
  return (
    <section
      ref={setNodeRef}
      className={cn(
        'flex min-h-0 w-[85%] shrink-0 flex-col rounded-lg border border-border/60 bg-muted/30 desktop:w-auto desktop:min-w-[280px] desktop:flex-1',
        isOver && 'border-primary/50 bg-primary/5',
      )}
    >
      <header className="flex items-center justify-between px-3 py-2">
        <span className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
          {labelOf(t.remarks.statuses, status)}
        </span>
        <Badge variant="secondary" className="font-normal tabular-nums">
          {remarks.length}
        </Badge>
      </header>
      <ScrollArea className="min-h-0 flex-1" viewportClassName="px-2 pb-2">
        <div className="space-y-2">
        {remarks.map((remark) => (
          <DraggableCard
            key={remark.id}
            remark={remark}
            onOpen={onOpen}
            disabled={busyIds?.has(remark.id)}
          />
        ))}
        {remarks.length === 0 ? (
          <p className="px-1 py-6 text-center text-[11px] text-muted-foreground">
            {t.common.empty}
          </p>
        ) : null}
        </div>
      </ScrollArea>
    </section>
  );
}

export function RemarksBoard({ remarks, onOpen, onMove, busyIds, className }: RemarksBoardProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const byStatus = useMemo(() => {
    const groups = new Map<RemarkStatus, Remark[]>();
    for (const status of REMARK_STATUSES) groups.set(status, []);
    for (const remark of remarks) {
      const key = (REMARK_STATUSES as string[]).includes(remark.status)
        ? (remark.status as RemarkStatus)
        : 'new';
      groups.get(key)!.push(remark);
    }
    return groups;
  }, [remarks]);

  const activeRemark = activeId ? remarks.find((remark) => remark.id === activeId) : null;

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const remark = event.active.data.current?.remark as Remark | undefined;
    const target = event.over?.id;
    if (!remark || typeof target !== 'string') return;
    if (!(REMARK_STATUSES as string[]).includes(target) || target === remark.status) return;
    onMove(remark, target as RemarkStatus);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={(event: DragStartEvent) => setActiveId(String(event.active.id))}
      onDragCancel={() => setActiveId(null)}
      onDragEnd={handleDragEnd}
    >
      <ScrollArea className={cn('h-full min-h-0', className)}>
        <div className="flex h-full min-h-0 w-max gap-3">
        {REMARK_STATUSES.map((status) => (
          <Column
            key={status}
            status={status}
            remarks={byStatus.get(status) ?? []}
            onOpen={onOpen}
            busyIds={busyIds}
          />
        ))}
        </div>
      </ScrollArea>
      <DragOverlay dropAnimation={null}>
        {activeRemark ? (
          <div className="w-[264px]">
            <RemarkCardBody remark={activeRemark} dragging />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
